import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { useSAL1020_R01Store } from "./Store_SAL1020_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultSAL1020_R01DetailDto, SAL1020_R01DetailDto } from "./Dto_SAL1020_R01";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL1020_R01-detail-grid-column-state-v2';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL1020_R01DetailDto> | null>;
}

export const DetailTable_SAL1020_R01 = (props: Props) => {

	const { gridRef } = props;

	const { 
		SAL1020_R01DetailList, 
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useSAL1020_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SAL1020_R01DetailList,
		defaultGridData: defaultSAL1020_R01DetailDto,
		selectedRow: [],
	});

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		modelNo: '모델명',
		brandNm: '브랜드',
		itemGroupNm: '품목그룹',
		itemAccountNm: '품목계정',
		orderUnitNm: '단위',
		orderQty: '수량',
		cancelQty: '취소량',
		orderPrice: '단가',
		dcAmt: '할인액',
		itemAmt: '매출금액',
	}

	const baseColumnDefs: ColDef<SAL1020_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 180,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '모델명', colId: 'modelNo', field: 'modelNo', width: 120,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '브랜드', colId: 'brandNm', field: 'brandNm', width: 100,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '품목그룹', colId: 'itemGroupNm', field: 'itemGroupNm', width: 100,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '품목계정', colId: 'itemAccountNm', field: 'itemAccountNm', width: 100,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '단위', colId: 'orderUnitNm', field: 'orderUnitNm', width: 80,
			headerClass: aggridHeaderClass,
			editable: false,
		},
		{
			headerName: '수량', colId: 'orderQty', field: 'orderQty', width: 100,
			headerClass: aggridHeaderClass,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'orderQty'),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '취소량', colId: 'cancelQty', field: 'cancelQty', width: 100,
			headerClass: aggridHeaderClass,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'cancelQty'),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '단가', colId: 'orderPrice', field: 'orderPrice', width: 120,
			headerClass: aggridHeaderClass,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'orderPrice'),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '할인액', colId: 'dcAmt', field: 'dcAmt', width: 120,
			headerClass: aggridHeaderClass,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'dcAmt'),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => {
				if (!params.value || params.value === 0) return '-';
				return params.value?.toLocaleString() ?? '';
			},
		},
		{
			headerName: '매출금액', colId: 'itemAmt', field: 'itemAmt', width: 120,
			headerClass: aggridHeaderClass,
			editable: false,
			cellStyle: (p) => getCellStyle(p, 'itemAmt'),
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL1020_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL1020_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRowData = useMemo(() => {
		const totalQty: number = SAL1020_R01DetailList
			.reduce((sum, item) => sum + (Number(item.orderQty) || 0), 0);
		const totalCancelQty: number = SAL1020_R01DetailList
			.reduce((sum, item) => sum + (Number(item.cancelQty) || 0), 0);
		const totalAmt: number = SAL1020_R01DetailList
			.reduce((sum, item) => sum + (Number(item.itemAmt) || 0), 0);

		return [{
			itemCd: SAL1020_R01DetailList.length.toLocaleString() + "건",
			orderQty: totalQty,
			cancelQty: totalCancelQty,
			itemAmt: totalAmt
		}];	  
	}, [SAL1020_R01DetailList]);

	return (
		<GridContextWrapper<SAL1020_R01DetailDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={() => {}}
			defaultRowData={defaultSAL1020_R01DetailDto}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL1020_R01DetailDto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				rowClassRules={createRowClassRules<SAL1020_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: [],
					deletedList: []
				})}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRowData}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	)
}
